import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useDropzone } from "react-dropzone";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { ExecutionPipeline } from "@/components/ExecutionPipeline";
import { StatusChip, Chip } from "@/components/StatusChip";
import { Send, Paperclip, Loader2, ArrowLeft, Wallet, ReceiptText, Clock } from "lucide-react";
import { AriaMark } from "@/components/AriaMark";
import { api, API, fmtUSD, fmtDate, fmtTime, initials } from "@/lib/api";
import { toast } from "sonner";

const PIPELINE_STAGES = [
  "Receipt uploaded", "OCR running", "Merchant recognized", "Policy matching",
  "Calendar lookup", "Corporate card matched", "GL assigned", "Expense submitted",
];
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export default function Portal() {
  const [summary, setSummary] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [pipeline, setPipeline] = useState(null);
  const convIdRef = useRef(null);
  const bottomRef = useRef(null);

  const scrollDown = () => setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 60);

  const loadSummary = () => api.get("/portal/summary").then((r) => {
    setSummary(r.data);
    convIdRef.current = r.data.conversation_id;
    if (r.data.conversation_id) {
      api.get(`/aria/conversations/${r.data.conversation_id}`).then((c) => setMessages(c.data.messages || [])).catch(() => {});
    }
  }).catch(() => {});

  useEffect(() => { loadSummary(); /* eslint-disable-line */ }, []);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", text, ts: new Date().toISOString() }]);
    setStreaming(true);
    const sid = `s-${Date.now()}`;
    setMessages((m) => [...m, { id: sid, role: "aria", text: "", ts: new Date().toISOString(), streaming: true }]);
    scrollDown();
    try {
      const res = await fetch(`${API}/aria/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conversation_id: convIdRef.current, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "", acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const evt = JSON.parse(line.slice(5).trim());
          if (evt.type === "start") convIdRef.current = evt.conversation_id;
          else if (evt.type === "delta") {
            acc += evt.content;
            const t = acc;
            setMessages((m) => m.map((msg) => (msg.id === sid ? { ...msg, text: t } : msg)));
            scrollDown();
          } else if (evt.type === "done") {
            setMessages((m) => m.map((msg) => (msg.id === sid ? { ...msg, streaming: false } : msg)));
          }
        }
      }
    } catch {
      setMessages((m) => m.map((msg) => (msg.id === sid ? { ...msg, text: "I hit a snag — try again in a moment.", streaming: false } : msg)));
    } finally {
      setStreaming(false);
      scrollDown();
    }
  };

  const handleFile = useCallback(async (file) => {
    if (!file || pipeline) return;
    if (!file.type.startsWith("image/")) { toast.error("Please upload a receipt image"); return; }
    const dataUrl = await new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(file); });
    setMessages((m) => [...m, { id: `u-${Date.now()}`, role: "user", text: "Here's my receipt.", ts: new Date().toISOString(), attachment: { preview: dataUrl } }]);
    setPipeline({ stage: 0, done: false });
    scrollDown();
    const timer = setInterval(() => setPipeline((p) => (p && !p.done && p.stage < 1 ? { ...p, stage: p.stage + 1 } : p)), 1100);
    try {
      const r = await api.post("/aria/receipt", { conversation_id: convIdRef.current, image_base64: dataUrl, filename: file.name }, { timeout: 120000 });
      clearInterval(timer);
      convIdRef.current = r.data.conversation_id;
      for (let s = 2; s < PIPELINE_STAGES.length; s++) { await sleep(360); setPipeline({ stage: s, done: false }); }
      await sleep(360);
      setPipeline({ stage: PIPELINE_STAGES.length, done: true });
      setMessages((m) => [...m, { id: `a-${Date.now()}`, role: "aria", text: r.data.reply, ts: new Date().toISOString() }]);
      toast.success(r.data.auto_approved ? "Expense filed and auto-approved" : "Expense filed — in review");
      loadSummary();
      await sleep(1200);
      setPipeline(null);
    } catch (e) {
      clearInterval(timer);
      setPipeline(null);
      toast.error("Receipt processing failed");
    } finally {
      scrollDown();
    }
  }, [pipeline]); // eslint-disable-line

  const { getRootProps, getInputProps, isDragActive, open: openPicker } = useDropzone({
    onDrop: (files) => handleFile(files[0]), noClick: true, noKeyboard: true, accept: { "image/*": [] },
  });

  const emp = summary?.employee;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="portal-page">
      {/* header */}
      <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-[#222] bg-[rgba(10,10,10,0.8)] px-4 backdrop-blur-md sm:gap-3 sm:px-5">
        <div className="flex h-7 w-7 items-center justify-center">
          <AriaMark size={28} />
        </div>
        <span className="text-[13.5px] font-semibold tracking-tight">Aria</span>
        <span className="hidden sm:inline-flex"><Chip tone="blue">Employee Portal</Chip></span>
        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          {emp && (
            <div className="flex items-center gap-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-full text-[9px] font-bold"
                style={{ background: `${emp.avatar_color}22`, color: emp.avatar_color }}>{initials(emp.name)}</span>
              <span className="hidden text-[12px] text-[#c9c9c9] sm:inline">{emp.name}</span>
            </div>
          )}
          <Link to="/" data-testid="portal-back-to-admin" className="flex items-center gap-1 rounded-lg border border-[#2a2a2a] px-2.5 py-1 text-[11.5px] text-[#A3A3A3] hover:bg-[#1c1c1c] hover:text-white">
            <ArrowLeft size={11} /> Admin view
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-[1300px] px-4 py-6 sm:px-6">
        {!summary ? (
          <div className="grid gap-4 lg:grid-cols-3"><Skeleton className="h-[70vh] rounded-[14px] bg-[#141414] lg:col-span-2" /><Skeleton className="h-[70vh] rounded-[14px] bg-[#141414]" /></div>
        ) : (
          <>
            <div className="mb-5">
              <h1 className="text-xl font-semibold tracking-tight">Hi {emp.name.split(" ")[0]}.</h1>
              <p className="mt-1 text-[13px] text-[#A3A3A3]">
                {summary.stats.pending_count > 0
                  ? <>Aria is waiting on <span className="text-[#FBBF24] font-medium">{summary.stats.pending_count} item{summary.stats.pending_count > 1 ? "s" : ""}</span> from you — drop the receipts below and you're done.</>
                  : "You're all caught up. Aria is handling everything else."}
              </p>
            </div>

            <div className="grid gap-4 lg:grid-cols-3">
              {/* chat */}
              <div {...getRootProps()} className={`relative flex flex-col rounded-[14px] border bg-[#111111] card-shadow lg:col-span-2 ${isDragActive ? "border-[rgba(79,142,247,0.6)]" : "border-[#222]"}`}
                style={{ height: "calc(100vh - 210px)", minHeight: 480 }} data-testid="portal-chat-container">
                <input {...getInputProps()} data-testid="portal-file-input" />
                {isDragActive && (
                  <div className="absolute inset-0 z-20 flex items-center justify-center rounded-[14px] bg-[rgba(10,10,10,0.85)] backdrop-blur-sm">
                    <div className="rounded-2xl border border-dashed border-[rgba(79,142,247,0.5)] bg-[rgba(79,142,247,0.08)] px-8 py-6 text-center">
                      <AriaMark size={36} className="mx-auto" />
                      <div className="mt-2 text-[13px] font-medium">Drop it — Aria files everything</div>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-2.5 border-b border-[#1e1e1e] px-4 py-3">
                  <AriaMark size={28} />
                  <div>
                    <div className="text-[13px] font-medium leading-none">Aria</div>
                    <div className="mt-0.5 flex items-center gap-1 text-[10px] text-[#4ADE80]"><span className="h-1 w-1 rounded-full bg-[#22C55E] pulse-dot" /> your expenses, handled</div>
                  </div>
                </div>
                <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4" data-testid="portal-messages">
                  {messages.map((msg) => {
                    const isAria = msg.role === "aria";
                    return (
                      <motion.div key={msg.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={`flex gap-2.5 ${isAria ? "" : "flex-row-reverse"}`}>
                        {isAria ? (
                          <AriaMark size={28} className="mt-1" />
                        ) : (
                          <div className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[9px] font-bold" style={{ background: `${emp.avatar_color}22`, color: emp.avatar_color }}>{initials(emp.name)}</div>
                        )}
                        <div className={`max-w-[75%] ${isAria ? "" : "text-right"}`}>
                          <div className={`inline-block rounded-2xl px-3.5 py-2.5 text-left text-[13px] leading-relaxed ${isAria ? "border border-[#222] bg-[#141414] text-[#e5e5e5]" : "bg-[#4F8EF7] text-white"}`}>
                            {msg.attachment?.preview && <img src={msg.attachment.preview} alt="receipt" className="mb-2 max-h-40 rounded-lg border border-[rgba(255,255,255,0.15)]" />}
                            {msg.text}
                            {msg.streaming && <span className="ml-0.5 inline-block h-3.5 w-[2px] animate-pulse bg-[#4F8EF7] align-middle" />}
                          </div>
                          <div className="mt-1 text-[10px] text-[#555]">{msg.ts ? fmtTime(msg.ts) : ""}</div>
                        </div>
                      </motion.div>
                    );
                  })}
                  <AnimatePresence>
                    {pipeline && (
                      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                        className="ml-9 max-w-[75%] rounded-2xl border border-[rgba(79,142,247,0.2)] bg-[rgba(79,142,247,0.04)] px-4 py-3.5">
                        <div className="mb-2.5 flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-[#7FAEF9]">
                          <Loader2 size={11} className={pipeline.done ? "hidden" : "animate-spin"} />
                          {pipeline.done ? "Pipeline complete" : "Aria is processing…"}
                        </div>
                        <ExecutionPipeline stages={PIPELINE_STAGES} current={pipeline.stage} done={pipeline.done} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                  <div ref={bottomRef} />
                </div>
                <div className="border-t border-[#1e1e1e] p-3">
                  <div className="flex items-end gap-2 rounded-2xl border border-[#2a2a2a] bg-[#161616] p-2 focus-within:border-[rgba(79,142,247,0.4)]">
                    <button onClick={openPicker} data-testid="portal-attach-button" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-[#A3A3A3] hover:bg-[#222] hover:text-white"><Paperclip size={15} /></button>
                    <Textarea value={input} onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                      placeholder="Message Aria — or drop a receipt…" rows={1} data-testid="portal-chat-input"
                      className="min-h-[36px] max-h-32 flex-1 resize-none border-0 bg-transparent p-1.5 text-[13px] text-white placeholder:text-[#555] focus-visible:ring-0" />
                    <button onClick={sendMessage} disabled={!input.trim() || streaming} data-testid="portal-chat-send-button"
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl btn-gradient text-white disabled:opacity-30"><Send size={14} /></button>
                  </div>
                </div>
              </div>

              {/* right rail */}
              <div className="space-y-4 lg:max-h-[calc(100vh-210px)] lg:overflow-y-auto">
                {/* pending actions */}
                <div className="rounded-[14px] border border-[#222] bg-[#111111] card-shadow">
                  <div className="flex items-center gap-2 border-b border-[#1e1e1e] px-4 py-3 text-[13px] font-medium"><Clock size={13} className="text-[#FBBF24]" /> Pending actions</div>
                  <div className="space-y-2 p-3" data-testid="portal-pending-actions">
                    {summary.pending_actions.length === 0 && <div className="px-2 py-2 text-[12px] text-[#6b6b6b]">Nothing pending — nice.</div>}
                    {summary.pending_actions.map((e) => (
                      <div key={e.id} className="rounded-xl border border-[rgba(245,158,11,0.2)] bg-[rgba(245,158,11,0.04)] px-3 py-2.5">
                        <div className="flex items-center justify-between">
                          <span className="text-[12.5px] font-medium">{e.merchant}</span>
                          <span className="text-[12.5px] font-semibold tabular-nums">{fmtUSD(e.amount)}</span>
                        </div>
                        <div className="mt-0.5 text-[11px] text-[#A3A3A3]">Aria is waiting for your receipt · {fmtDate(e.date)}</div>
                        <button onClick={openPicker} data-testid={`portal-upload-receipt-${e.id}`}
                          className="mt-2 rounded-lg bg-[#F59E0B] px-2.5 py-1 text-[11px] font-medium text-black hover:bg-[#fbbf24]">Upload receipt</button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* my expenses */}
                <div className="rounded-[14px] border border-[#222] bg-[#111111] card-shadow">
                  <div className="flex items-center gap-2 border-b border-[#1e1e1e] px-4 py-3 text-[13px] font-medium"><ReceiptText size={13} className="text-[#7FAEF9]" /> My expenses</div>
                  <div className="divide-y divide-[#181818]" data-testid="portal-expenses-list">
                    {summary.expenses.slice(0, 6).map((e) => (
                      <div key={e.id} className="flex items-center justify-between px-4 py-2.5">
                        <div>
                          <div className="text-[12.5px] text-white">{e.merchant}</div>
                          <div className="text-[10.5px] text-[#6b6b6b]">{fmtDate(e.date)} · {e.category}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <StatusChip status={e.status} />
                          <span className="w-16 text-right text-[12px] font-medium tabular-nums">{fmtUSD(e.amount)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* reimbursements */}
                <div className="rounded-[14px] border border-[#222] bg-[#111111] card-shadow">
                  <div className="border-b border-[#1e1e1e] px-4 py-3 text-[13px] font-medium">My reimbursements</div>
                  <div className="divide-y divide-[#181818]" data-testid="portal-reimbursements-list">
                    {summary.reimbursements.map((r) => (
                      <div key={r.id} className="flex items-center justify-between px-4 py-2.5">
                        <div>
                          <div className="text-[12.5px] text-white">{r.merchant}</div>
                          <div className="text-[10.5px] text-[#6b6b6b]">{fmtDate(r.date)}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Chip tone={r.status === "paid" ? "green" : "amber"}>{r.status}</Chip>
                          <span className="w-16 text-right text-[12px] font-medium tabular-nums">{fmtUSD(r.amount)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* stipends */}
                <div className="rounded-[14px] border border-[#222] bg-[#111111] card-shadow">
                  <div className="flex items-center gap-2 border-b border-[#1e1e1e] px-4 py-3 text-[13px] font-medium"><Wallet size={13} className="text-[#4ADE80]" /> My stipends</div>
                  <div className="space-y-2.5 p-4" data-testid="portal-stipends-list">
                    {summary.stipends.map((s) => {
                      const pct = Math.min(100, (s.used / s.limit) * 100);
                      return (
                        <div key={s.id}>
                          <div className="flex justify-between text-[11.5px]"><span className="text-[#c9c9c9]">{s.name}</span><span className="tabular-nums text-[#6b6b6b]">{fmtUSD(s.limit - s.used)} left</span></div>
                          <div className="mt-1 h-1 overflow-hidden rounded-full bg-[#222]"><div className="h-full rounded-full bg-[#4F8EF7]" style={{ width: `${pct}%` }} /></div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
